// frontend/src/components/charts/OutstandingPOsTable.jsx
// Compact AG-Grid table showing outstanding purchase orders.
// Embedded in the Home page dashboard tile (half-width).

import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenPurchaseOrder } from '../../hooks/useOpenPurchaseOrder';
import { AgGridReact } from 'ag-grid-react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { dashboardColDef, dashboardGridProps, copyOnlyContextMenuItems, actionColumn, flexColumn } from '../../utils/gridDefaults';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import '../../styles/grid-page.css';

function OutstandingPOsTable({ useItemsData }) {
  const { t } = useTranslation();
  const openPurchaseOrder = useOpenPurchaseOrder();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatDate } = useFormatters();
  const { data, loading, error } = useItemsData();
  // Columns are built before the payload arrives; every payload carries a
  // currency (`core.utils.resolve_currency` raises for a store without one).
  const currencySuffix = data ? ` (${data.currency})` : '';

  // Stable identity — see ForecastGrid's getRowId for the AG Grid rationale.
  const getRowId = useCallback((params) => String(params.data.po_id), []);

  const columnDefs = useMemo(() => [
    {
      field: 'po_number',
      headerName: t('home.outstandingPOs.poNumber'),
    },
    {
      field: 'supplier_name',
      headerName: t('home.outstandingPOs.supplier'),
      ...flexColumn(),
      tooltipField: 'supplier_name',
    },
    {
      field: 'location_name',
      headerName: t('home.outstandingPOs.location'),
      tooltipField: 'location_name',
      ...flexColumn(),
      minWidth: 100,
    },
    {
      field: 'need_by_date',
      headerName: t('home.outstandingPOs.expected'),
      type: 'dateColumn',
      sort: 'asc',
      maxWidth: 110,
      valueFormatter: (params) => {
        if (!params.value) return '—';
        return formatDate(params.value);
      },
    },
    {
      field: 'outstanding_qty',
      headerName: t('home.outstandingPOs.poQty'),
      type: 'numberColumn',
    },
    {
      field: 'outstanding_value',
      headerName: t('home.outstandingPOs.poValue') + currencySuffix,
      type: 'decimalColumn',
    },
    actionColumn({
      cellRenderer: (params) => (
        <Button
          className="grid-action-btn"
          variant="contained"
          onMouseDown={e => e.stopPropagation()}
          onClick={() => openPurchaseOrder(params.data.po_id, { receive: true })}
        >
          {t('purchaseOrders.actions.receive')}
        </Button>
      ),
    }),
  ], [t, openPurchaseOrder, formatDate, currencySuffix]);

  // due_status comes from the backend (core.date_utils.classify_due_status,
  // anchored on the store's timezone). Don't recompute it here: the browser's
  // midnight is not the store's.
  const getRowClass = useCallback((params) => {
    if (params.data?.due_status === 'overdue') {
      return 'po-overdue-row';
    }
    return undefined;
  }, []);

  if (loading) return <div className="dashboard-grid-wrapper"><div className="chart-state"><CircularProgress size={36} /></div></div>;
  if (error) return <div className="dashboard-grid-wrapper"><div className="chart-state"><Typography color="error">{error}</Typography></div></div>;
  if (data.items.length === 0) return (
    <div className="dashboard-grid-wrapper">
      <div className="chart-state">
        <Typography color="textSecondary">{t('home.outstandingPOs.noData')}</Typography>
      </div>
    </div>
  );

  return (
    <div className="dashboard-grid-wrapper">
      {gridTheme && (
        <AgGridReact
          {...dashboardGridProps}
          theme={gridTheme}
          rowData={data.items}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          defaultColDef={dashboardColDef}
          getRowClass={getRowClass}
          getRowId={getRowId}
          getContextMenuItems={copyOnlyContextMenuItems}
        />
      )}
    </div>
  );
}

export default OutstandingPOsTable;
